import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card } from '../../ui/card';
import { Avatar } from '../../ui/avatar';
import { Modal, ModalBody, ModalFooter } from '../../ui/modal';
import { Loader2, Palette, User, CreditCard, Key, Mail, Send, Lock, CheckCircle } from 'lucide-react';
import { supabase } from '../../../supabaseClient';

const UserForm = ({ 
  user = null, 
  onSubmit, 
  onCancel, 
  loading = false,
  mode = 'create' // 'create', 'edit', 'clone'
}) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    username: '',
    avatar_type: 'initials',
    avatar_color: '#6366f1',
    avatar_initials: '',
    subscription_tier_id: '',
    billing_cycle: 'monthly',
    password: ''
  });
  
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Available avatar colors
  const avatarColors = [
    '#ef4444', '#f97316', '#eab308', '#22c55e', 
    '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', 
    '#ec4899', '#f43f5e'
  ];

  // Load subscription tiers
  useEffect(() => {
    const fetchSubscriptionTiers = async () => {
      const { data, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      if (!error && data) {
        setSubscriptionTiers(data);
      }
    };

    fetchSubscriptionTiers();
  }, []);

  // Populate form when editing or cloning
  useEffect(() => {
    if (user && (mode === 'edit' || mode === 'clone')) {
      setFormData({
        email: mode === 'clone' ? '' : (user.email || ''),
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        username: mode === 'clone' ? '' : (user.username || ''),
        avatar_type: user.avatar_type || 'initials',
        avatar_color: user.avatar_color || '#6366f1',
        avatar_initials: user.avatar_initials || '',
        subscription_tier_id: user.user_subscriptions?.[0]?.subscription_tier_id || '',
        billing_cycle: user.user_subscriptions?.[0]?.billing_cycle || 'monthly',
        password: ''
      });
      
      // Update initials if needed
      updateInitials(user.first_name || '', user.last_name || '');
    }
  }, [user, mode]);

  const updateInitials = (firstName, lastName) => {
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    setFormData(prev => ({ ...prev, avatar_initials: initials }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-update initials when name changes
      if (name === 'first_name' || name === 'last_name') {
        const firstName = name === 'first_name' ? value : prev.first_name;
        const lastName = name === 'last_name' ? value : prev.last_name;
        updateInitials(firstName, lastName);
      }
      
      return updated;
    });

    // Clear errors when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }

    if (mode === 'create' && !formData.password.trim()) {
      errors.password = 'Password is required';
    }

    if (mode === 'create' && formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Password change handlers
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm the new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePassword()) {
      return;
    }

    setIsChangingPassword(true);
    try {
      // Use secure RPC function for admin password changes with audit logging
      const { error } = await supabase.rpc('admin_change_user_password', {
        target_user_id: user.user_id || user.id,
        new_password: passwordData.newPassword,
        admin_reason: 'Admin password change via UI'
      });

      if (error) {
        throw error;
      }

      // Reset password form
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      setShowPasswordChange(false);
      
      // Show success modal
      setSuccessMessage('Password updated successfully');
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordErrors({ general: error.message || 'Failed to update password' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSendPasswordReset = async () => {
    try {
      // Use secure RPC function for admin password reset with audit logging
      const { error } = await supabase.rpc('admin_send_password_reset', {
        target_user_id: user.user_id || user.id,
        admin_reason: 'Admin initiated password reset via UI'
      });
      
      if (error) {
        throw error;
      }
      
      // Also send the actual password reset email
      const { error: emailError } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/change-password`
      });
      
      if (emailError) {
        throw emailError;
      }
      
      setSuccessMessage('Password reset email sent successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setSuccessMessage('Failed to send password reset email: ' + error.message);
      setShowSuccessModal(true);
    }
  };

  const handleSendMagicLink = async () => {
    try {
      // Use secure RPC function for admin magic link with audit logging
      const { error } = await supabase.rpc('admin_send_magic_link', {
        target_user_id: user.user_id || user.id,
        admin_reason: 'Admin initiated magic link via UI'
      });
      
      if (error) {
        throw error;
      }
      
      // Also send the actual magic link
      const { error: emailError } = await supabase.auth.signInWithOtp({
        email: user.email
      });
      
      if (emailError) {
        throw emailError;
      }
      
      setSuccessMessage('Magic link sent successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Magic link error:', error);
      setSuccessMessage('Failed to send magic link: ' + error.message);
      setShowSuccessModal(true);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card className="p-4">
        <div className="flex items-center mb-4">
          <User className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Basic Information
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={formErrors.email ? 'border-red-500' : ''}
              placeholder="user@example.com"
            />
            {formErrors.email && (
              <p className="text-sm text-red-600 mt-1">{formErrors.email}</p>
            )}
          </div>

          {mode === 'create' && (
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                className={formErrors.password ? 'border-red-500' : ''}
                placeholder="Minimum 6 characters"
              />
              {formErrors.password && (
                <p className="text-sm text-red-600 mt-1">{formErrors.password}</p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className={formErrors.first_name ? 'border-red-500' : ''}
              placeholder="John"
            />
            {formErrors.first_name && (
              <p className="text-sm text-red-600 mt-1">{formErrors.first_name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              className={formErrors.last_name ? 'border-red-500' : ''}
              placeholder="Doe"
            />
            {formErrors.last_name && (
              <p className="text-sm text-red-600 mt-1">{formErrors.last_name}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className={formErrors.username ? 'border-red-500' : ''}
              placeholder="johndoe"
            />
            {formErrors.username && (
              <p className="text-sm text-red-600 mt-1">{formErrors.username}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Avatar Settings */}
      <Card className="p-4">
        <div className="flex items-center mb-4">
          <Palette className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Avatar Settings
          </h3>
        </div>

        <div className="flex items-center space-x-6">
          <div>
            <Avatar
              size="lg"
              initials={formData.avatar_initials}
              color={formData.avatar_color}
              type="initials"
            />
          </div>
          
          <div className="flex-1">
            <Label htmlFor="avatar_color">Avatar Color</Label>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {avatarColors.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, avatar_color: color }))}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formData.avatar_color === color 
                      ? 'border-gray-900 dark:border-gray-100 scale-110' 
                      : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Account Recovery - Only show for existing users */}
      {mode === 'edit' && user && (
        <Card className="p-4">
          <div className="flex items-center mb-4">
            <Key className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Account Recovery
            </h3>
          </div>
          
          {/* Recovery Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSendPasswordReset}
              className="flex items-center justify-center"
            >
              <Mail className="h-4 w-4 mr-2" />
              Reset Password
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSendMagicLink}
              className="flex items-center justify-center"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Magic Link
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowPasswordChange(!showPasswordChange)}
              className="flex items-center justify-center"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </div>

          {/* Password Change Form */}
          {showPasswordChange && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={passwordErrors.newPassword ? 'border-red-500' : ''}
                    placeholder="Enter new password"
                  />
                  {passwordErrors.newPassword && (
                    <p className="text-sm text-red-600 mt-1">{passwordErrors.newPassword}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={passwordErrors.confirmPassword ? 'border-red-500' : ''}
                    placeholder="Confirm new password"
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="text-sm text-red-600 mt-1">{passwordErrors.confirmPassword}</p>
                  )}
                </div>
              </div>
              
              {passwordErrors.general && (
                <p className="text-sm text-red-600 mt-2">{passwordErrors.general}</p>
              )}
              
              <div className="flex justify-end space-x-2 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordData({ newPassword: '', confirmPassword: '' });
                    setPasswordErrors({});
                  }}
                  disabled={isChangingPassword}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handlePasswordSubmit}
                  disabled={isChangingPassword}
                >
                  {isChangingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Update Password
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Subscription */}
      <Card className="p-4">
        <div className="flex items-center mb-4">
          <CreditCard className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Subscription
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="subscription_tier_id">Subscription Tier</Label>
            <select
              id="subscription_tier_id"
              name="subscription_tier_id"
              value={formData.subscription_tier_id}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select a tier</option>
              {subscriptionTiers.map(tier => (
                <option key={tier.id} value={tier.id}>
                  {tier.display_name} - ${tier.price_monthly}/month
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="billing_cycle">Billing Cycle</Label>
            <select
              id="billing_cycle"
              name="billing_cycle"
              value={formData.billing_cycle}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting || loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || loading}
        >
          {(isSubmitting || loading) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {mode === 'edit' ? 'Update User' : 'Create User'}
        </Button>
      </div>
      
      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        className="max-w-sm"
      >
        <ModalBody>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <p className="text-gray-900 dark:text-gray-100">
              {successMessage}
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => setShowSuccessModal(false)}>
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </form>
  );
};

export default UserForm;
